import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useAuth0 } from '@auth0/auth0-react'
import { useQuery } from '@tanstack/react-query'
import {
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  PencilIcon,
  TrashIcon,
  TagIcon,
  UserIcon,
  CalendarIcon,
  PhotoIcon,
  BookOpenIcon
} from '@heroicons/react/24/outline'
import { LedgerEntry, useRailbirdLedgerApi, LedgerSearchParams } from '../../services/railbirdLedgerApi'
import { isAdmin } from '../../utils/roles'
import CreateLedgerEntryModal from '../../components/CreateLedgerEntryModal'
import EditLedgerEntryModal from '../../components/EditLedgerEntryModal'
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal'
import toast from 'react-hot-toast'

export default function RailbirdLedgerPage() {
  const { user } = useAuth0()
  const railbirdLedgerApi = useRailbirdLedgerApi()
  
  // State
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [selectedScribe, setSelectedScribe] = useState('')
  const [selectedYear, setSelectedYear] = useState<number | undefined>()
  const [showFilters, setShowFilters] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingEntry, setEditingEntry] = useState<LedgerEntry | null>(null)
  const [deletingEntry, setDeletingEntry] = useState<LedgerEntry | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const userIsAdmin = isAdmin(user)

  // Build search params
  const searchParams: LedgerSearchParams = {
    limit: 50,
    search: searchTerm || undefined,
    tags: selectedTags.length > 0 ? selectedTags : undefined,
    scribe: selectedScribe || undefined,
    year: selectedYear
  }

  // Queries
  const { data: entriesData, isLoading, error, refetch } = useQuery({
    queryKey: ['railbird-ledger-entries', searchParams],
    queryFn: () => railbirdLedgerApi.getEntries(searchParams),
    enabled: !!user
  })

  const entries = entriesData?.entries || []

  // Extract unique tags and scribes from entries
  const allTags = React.useMemo(() => {
    const tagSet = new Set<string>()
    entries.forEach(entry => {
      entry.tags.forEach(tag => tagSet.add(tag))
    })
    return Array.from(tagSet).sort()
  }, [entries])

  const allScribes = React.useMemo(() => {
    const scribeSet = new Set<string>()
    entries.forEach(entry => {
      scribeSet.add(entry.scribe)
    })
    return Array.from(scribeSet).sort()
  }, [entries])

  // Event handlers
  const handleCreateSuccess = () => {
    setShowCreateModal(false)
    refetch()
    toast.success('Adventure logged successfully!')
  }

  const handleEditSuccess = () => {
    setEditingEntry(null)
    refetch()
    toast.success('Adventure updated successfully!')
  }

  const handleDeleteEntry = (entry: LedgerEntry) => {
    setDeletingEntry(entry)
  }

  const confirmDeleteEntry = async () => {
    if (!deletingEntry) return
    try {
      setIsDeleting(true)
      await railbirdLedgerApi.deleteEntry(deletingEntry.id)
      toast.success(`Adventure "${deletingEntry.title}" deleted successfully!`)
      setDeletingEntry(null)
      refetch()
    } catch (err) {
      console.error('Failed to delete entry:', err)
      toast.error('Failed to delete adventure.')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleTagToggle = (tag: string) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    )
  }

  const clearFilters = () => {
    setSearchTerm('')
    setSelectedTags([])
    setSelectedScribe('')
    setSelectedYear(undefined)
  }

  const hasActiveFilters = searchTerm || selectedTags.length > 0 || selectedScribe || selectedYear

  // Get unique years from entries for year filter
  const availableYears = Array.from(new Set(
    entries.map(entry => new Date(entry.createdAt).getFullYear())
  )).sort((a, b) => b - a)

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-emerald-700 dark:text-emerald-300" style={{ fontFamily: 'Georgia, serif' }}>
            Loading the ledger...
          </p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="text-center text-red-600 dark:text-red-400" style={{ fontFamily: 'Georgia, serif' }}>
          <p className="text-xl">Error loading the ledger</p>
          <p className="mt-2">{error.message}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100 dark:from-gray-900 dark:to-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          {/* Header */}
          <div className="text-center">
            <motion.h1 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-5xl font-bold text-emerald-800 dark:text-emerald-200 mb-4"
              style={{ fontFamily: 'Georgia, serif' }}
            >
              The Railbird Ledger
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-xl text-emerald-700 dark:text-emerald-300 max-w-3xl mx-auto"
              style={{ fontFamily: 'Georgia, serif' }}
            >
              Chronicles of adventures, memorable moments, and tales from the rails. 
              A collection of stories that capture the spirit of railbird life.
            </motion.p>
          </div>

          {/* Search and Filters */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 border-2 border-emerald-200 dark:border-emerald-700"
          >
            <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
              {/* Search Bar */}
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-emerald-500" />
                  <input
                    type="text"
                    placeholder="Search adventures..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-emerald-300 dark:border-emerald-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    style={{ fontFamily: 'Georgia, serif' }}
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex items-center space-x-2 px-4 py-2 border rounded-lg transition-colors ${
                    hasActiveFilters || showFilters
                      ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300'
                      : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                  style={{ fontFamily: 'Georgia, serif' }}
                >
                  <FunnelIcon className="w-4 h-4" />
                  <span>Filters</span>
                  {hasActiveFilters && (
                    <span className="bg-emerald-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                      {(selectedTags.length || 0) + (selectedScribe ? 1 : 0) + (selectedYear ? 1 : 0) + (searchTerm ? 1 : 0)}
                    </span>
                  )}
                </button>
                
                {userIsAdmin && (
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="flex items-center space-x-2 px-6 py-3 bg-emerald-600 text-white rounded-lg shadow-md hover:bg-emerald-700 transition-colors font-semibold"
                    style={{ fontFamily: 'Georgia, serif' }}
                  >
                    <PlusIcon className="w-5 h-5" />
                    <span>New Adventure</span>
                  </button>
                )}
              </div>
            </div>

            {/* Expanded Filters */}
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-6 pt-6 border-t border-emerald-200 dark:border-emerald-700 space-y-4"
              >
                {/* Tags Filter */}
                {allTags.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-emerald-700 dark:text-emerald-300 mb-2" style={{ fontFamily: 'Georgia, serif' }}>
                      Tags
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {allTags.map(tag => (
                        <button
                          key={tag}
                          onClick={() => handleTagToggle(tag)}
                          className={`px-3 py-1 rounded-full text-sm transition-colors ${
                            selectedTags.includes(tag)
                              ? 'bg-emerald-500 text-white'
                              : 'bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-200 dark:hover:bg-emerald-800'
                          }`}
                          style={{ fontFamily: 'Georgia, serif' }}
                        >
                          {tag}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Scribe and Year Filters */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Scribe Filter */}
                  {allScribes.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-emerald-700 dark:text-emerald-300 mb-2" style={{ fontFamily: 'Georgia, serif' }}>
                        Scribe
                      </label>
                      <select
                        value={selectedScribe}
                        onChange={(e) => setSelectedScribe(e.target.value)}
                        className="w-full px-3 py-2 border border-emerald-300 dark:border-emerald-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        style={{ fontFamily: 'Georgia, serif' }}
                      >
                        <option value="">All Scribes</option>
                        {allScribes.map(scribe => (
                          <option key={scribe} value={scribe}>{scribe}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Year Filter */}
                  {availableYears.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-emerald-700 dark:text-emerald-300 mb-2" style={{ fontFamily: 'Georgia, serif' }}>
                        Year
                      </label>
                      <select
                        value={selectedYear || ''}
                        onChange={(e) => setSelectedYear(e.target.value ? parseInt(e.target.value) : undefined)}
                        className="w-full px-3 py-2 border border-emerald-300 dark:border-emerald-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        style={{ fontFamily: 'Georgia, serif' }}
                      >
                        <option value="">All Years</option>
                        {availableYears.map(year => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                {/* Clear Filters */}
                {hasActiveFilters && (
                  <div className="flex justify-end">
                    <button
                      onClick={clearFilters}
                      className="text-sm text-emerald-600 dark:text-emerald-400 hover:text-emerald-800 dark:hover:text-emerald-200 transition-colors"
                      style={{ fontFamily: 'Georgia, serif' }}
                    >
                      Clear all filters
                    </button>
                  </div>
                )}
              </motion.div>
            )}
          </motion.div>

          {/* Entries List */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="space-y-6"
          >
            {entries.length === 0 ? (
              <div className="text-center py-16">
                <BookOpenIcon className="w-16 h-16 text-emerald-300 dark:text-emerald-600 mx-auto mb-4" />
                <p className="text-xl text-emerald-700 dark:text-emerald-300 mb-2" style={{ fontFamily: 'Georgia, serif' }}>
                  No adventures found
                </p>
                <p className="text-emerald-600 dark:text-emerald-400" style={{ fontFamily: 'Georgia, serif' }}>
                  {hasActiveFilters ? 'Try adjusting your filters' : 'Be the first to chronicle a railbird adventure!'}
                </p>
              </div>
            ) : (
              entries.map((entry, index) => (
                <motion.article
                  key={entry.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * index }}
                  className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border-2 border-emerald-200 dark:border-emerald-700 overflow-hidden hover:shadow-xl transition-all duration-300"
                >
                  <div className="p-8">
                    {/* Entry Header */}
                    <div className="flex justify-between items-start mb-6">
                      <div className="flex-1 mr-4">
                        <h2 className="text-2xl font-bold text-emerald-800 dark:text-emerald-200 mb-2" style={{ fontFamily: 'Georgia, serif' }}>
                          {entry.title}
                        </h2>
                        <div className="flex items-center space-x-4 text-sm text-emerald-600 dark:text-emerald-400">
                          <div className="flex items-center space-x-1">
                            <UserIcon className="w-4 h-4" />
                            <span style={{ fontFamily: 'Georgia, serif' }}>Scribed by {entry.scribe}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <CalendarIcon className="w-4 h-4" />
                            <span style={{ fontFamily: 'Georgia, serif' }}>
                              {new Date(entry.createdAt).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </span>
                          </div>
                          {entry.images && entry.images.length > 0 && (
                            <div className="flex items-center space-x-1">
                              <PhotoIcon className="w-4 h-4" />
                              <span style={{ fontFamily: 'Georgia, serif' }}>{entry.images.length} photo{entry.images.length !== 1 ? 's' : ''}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {userIsAdmin && (
                        <div className="flex space-x-2">
                          <button
                            onClick={() => setEditingEntry(entry)}
                            className="p-2 text-emerald-600 hover:text-emerald-800 dark:text-emerald-400 dark:hover:text-emerald-200 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors"
                            title="Edit adventure"
                          >
                            <PencilIcon className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDeleteEntry(entry)}
                            className="p-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-200 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                            title="Delete adventure"
                          >
                            <TrashIcon className="w-5 h-5" />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Entry Photos */}
                    {entry.images && entry.images.length > 0 && (
                      <div className="mb-6">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                          {entry.images.map((image, imageIndex) => (
                            <div key={imageIndex} className="aspect-video rounded-lg overflow-hidden border-2 border-emerald-200 dark:border-emerald-700 shadow-md hover:shadow-lg transition-shadow">
                              <img
                                src={image}
                                alt={`${entry.title} - Photo ${imageIndex + 1}`}
                                className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Entry Body */}
                    <div className="prose prose-emerald dark:prose-invert max-w-none mb-6">
                      <div 
                        className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap"
                        style={{ fontFamily: 'Georgia, serif' }}
                      >
                        {entry.body}
                      </div>
                    </div>

                    {/* Entry Tags */}
                    {entry.tags.length > 0 && (
                      <div className="flex items-center space-x-2">
                        <TagIcon className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                        <div className="flex flex-wrap gap-2">
                          {entry.tags.map(tag => (
                            <span
                              key={tag}
                              className="px-2 py-1 bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300 rounded-full text-sm"
                              style={{ fontFamily: 'Georgia, serif' }}
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </motion.article>
              ))
            )}
          </motion.div>
        </motion.div>

        {/* Modals */}
        <CreateLedgerEntryModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateSuccess}
        />
        
        <EditLedgerEntryModal
          isOpen={!!editingEntry}
          onClose={() => setEditingEntry(null)}
          entry={editingEntry}
          onSuccess={handleEditSuccess}
        />

        <ConfirmDeleteModal
          isOpen={!!deletingEntry}
          onClose={() => setDeletingEntry(null)}
          onConfirm={confirmDeleteEntry}
          title="Delete Adventure"
          message={`Are you sure you want to delete the adventure "${deletingEntry?.title}"? This action cannot be undone.`}
          confirmText="Delete Adventure"
          isDeleting={isDeleting}
          countdownSeconds={5}
        />
      </div>
    </div>
  )
}
